/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, February 22nd 2022, 9:47:28 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { ReactNode } from "react";
import { FormConfiguration, FORM_STATUS_CHOICES, FORM_TYPES } from "lib/schemas";
import { FormAPIPatchForm, FormAPIPatchFormProps } from "form-builder/components/FormAPIPatchForm";
import { TranslatableTextFormInput, TranslatableTextAreaInput } from "translation/components";
import { DropDownFormInput, RadioFormInput, SwitchFormInput } from "ui-widgets/forms";
import { useToggleState } from "ui-widgets";

/**
 * FormInfoPopUpForm
 */

export interface FormInfoPopUpFormProps extends Omit<FormAPIPatchFormProps, "children"> {
  allowedFormTypes?: FormConfiguration["form_type"][];
  children?: (options: { handleOpen: () => void }) => ReactNode;
}

export const FormInfoPopUpForm = ({ allowedFormTypes = [], ...props }: FormInfoPopUpFormProps) => {
  const showFormTypeSelector = allowedFormTypes && allowedFormTypes?.length > 0;

  const formTypeOptionsKeys = allowedFormTypes.length > 0 ? allowedFormTypes : Object.keys(FORM_TYPES);

  const formTypeOptions = formTypeOptionsKeys.reduce(
    (acc, formType) => ({ ...acc, [formType]: FORM_TYPES[formType] }),
    {}
  );

  const { handleClose, handleOpen, open } = useToggleState();

  const formInfoPopUpOpenState = props.children ? open : props.formInfoPopUpOpenState;

  const onCloseForm = props.children ? handleClose : props.closeFormInfoPopUpFn;

  const onAPISaveFormInfoCallBackFn = props.children
    ? (formConfiguration: FormConfiguration) => {
        props.onAPISaveFormInfoCallBackFn(formConfiguration);
        handleClose();
      }
    : props.onAPISaveFormInfoCallBackFn;

  return (
    <>
      {props.children?.({ handleOpen })}

      <FormAPIPatchForm
        {...props}
        formInfoPopUpOpenState={formInfoPopUpOpenState}
        closeFormInfoPopUpFn={onCloseForm}
        onAPISaveFormInfoCallBackFn={onAPISaveFormInfoCallBackFn}
      >
        <TranslatableTextFormInput<FormConfiguration> schema_field_key="title" label="Title" />
        <TranslatableTextAreaInput<FormConfiguration> schema_field_key="intro" label="Description" />
        {showFormTypeSelector && (
          <DropDownFormInput<FormConfiguration>
            schema_field_key="form_type"
            label="Form Type"
            options={formTypeOptions}
          />
        )}
        <TranslatableTextFormInput<FormConfiguration> schema_field_key="button_text" label="Submit Button Text" />
        <RadioFormInput<FormConfiguration> schema_field_key="status" label="Status" options={FORM_STATUS_CHOICES} />
        <SwitchFormInput<FormConfiguration>
          schema_field_key="login_required"
          label="Is a login required to access this form?"
        />
        <SwitchFormInput<FormConfiguration>
          schema_field_key="archive_changed_data"
          label="Archive old data when form entries for this form are updated? Draft updates are not archived."
        />
        <TranslatableTextAreaInput<FormConfiguration> schema_field_key="response" label="Submission Response" />
        <SwitchFormInput<FormConfiguration>
          schema_field_key="enforce_editable_fields_on_resubmission"
          label="Enforce restriction on the fields one can edit when this form is resubmitted?"
        />
      </FormAPIPatchForm>
    </>
  );
};
